import {
    Box, Container, Heading, Text, VStack, HStack, Flex, Button, IconButton,
    Spinner, Alert, AlertIcon, useToast, Image,
    NumberInput, NumberInputField, Divider, Progress,
} from '@chakra-ui/react';
import { CopyIcon, CheckIcon } from '@chakra-ui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import ScrollToTop from '../components/ScrollToTop';
import { FaCheck, FaTimes, FaBoxOpen, FaExclamationCircle } from 'react-icons/fa';
import {
    buscarConsultaEstoquePorHash,
    responderConsultaEstoque,
    finalizarConsultaEstoque,
} from '../services/api';
import ProdutoImagem from '../components/ProdutoImagem';

const data = (valor) => (valor ? new Date(valor).toLocaleDateString('pt-BR') : '');

const emReais = (valor) => {
    const numero = Number(valor);
    if (!Number.isFinite(numero) || numero <= 0) return null;
    return numero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const copiarTexto = async (valor) => {
    const texto = String(valor ?? '');
    if (!texto) return false;

    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(texto);
            return true;
        }
    } catch (e) {
        // cai no plano B
    }

    try {
        const area = document.createElement('textarea');
        area.value = texto;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        const deu = document.execCommand('copy');
        document.body.removeChild(area);
        return deu;
    } catch (e) {
        return false;
    }
};

const BotaoCopiar = ({ rotulo, valor, copiado, onCopiar }) => (
    <IconButton
        aria-label={`Copiar ${rotulo}`}
        title={`Copiar ${rotulo}`}
        icon={copiado ? <CheckIcon boxSize={2.5} /> : <CopyIcon boxSize={3} />}
        size="xs"
        variant="ghost"
        color={copiado ? 'green.500' : 'orange.500'}
        _hover={{ bg: copiado ? 'green.50' : 'orange.50' }}
        minW="22px"
        h="22px"
        ml={1}
        verticalAlign="middle"
        onClick={() => onCopiar(valor)}
    />
);

const tempoParado = (desde) => {
    if (!desde) return '';
    const dias = Math.floor((Date.now() - new Date(desde).getTime()) / 86400000);
    if (dias < 1) return 'desde hoje';
    if (dias === 1) return 'há 1 dia';
    if (dias < 60) return `há ${dias} dias`;
    return `há ${Math.floor(dias / 30)} meses`;
};

// O teclado do celular deixa passar ponto, virgula e sinal, e o fornecedor
// digita "2,5" sem pensar. Quantidade de peca e inteiro positivo: tudo que
// nao for digito cai fora aqui, antes de virar estado.
const somenteInteiro = (v) => String(v ?? '').replace(/\D/g, '').replace(/^0+/, '');

// "Tenho" sem quantidade nao foi gravado no servidor, entao nao conta como
// respondido nem libera o envio.
const estaRespondido = (r) => (
    r?.resposta === 'nao_tem' || (r?.resposta === 'tem' && Number(r?.quantidade) > 0)
);

export default function ConsultaEstoqueFornecedor() {
    const { hash } = useParams();
    const toast = useToast();

    const [carregando, setCarregando] = useState(true);
    const [consulta, setConsulta] = useState(null);
    const [respostas, setRespostas] = useState({});
    const [salvando, setSalvando] = useState(null);
    const [enviando, setEnviando] = useState(false);
    // So destaca o que falta DEPOIS da primeira tentativa de envio: abrir a
    // pagina com tudo em vermelho e agressivo e nao ajuda ninguem.
    const [cobrando, setCobrando] = useState(false);
    const [copiado, setCopiado] = useState(null);

    const copiar = useCallback(async (chave, rotulo, valor) => {
        const deu = await copiarTexto(valor);

        if (!deu) {
            toast({ title: 'Não foi possível copiar', status: 'error', duration: 2500, isClosable: true });
            return;
        }

        setCopiado(chave);
        toast({
            title: `${rotulo} copiado`,
            description: String(valor),
            status: 'success',
            duration: 1800,
            isClosable: true,
            position: 'top',
        });
    }, [toast]);

    useEffect(() => {
        if (!copiado) return undefined;
        const t = setTimeout(() => setCopiado(null), 1800);
        return () => clearTimeout(t);
    }, [copiado]);
    const cardsRef = useRef({});

    const carregar = useCallback(async () => {
        setCarregando(true);
        const r = await buscarConsultaEstoquePorHash(hash);
        setConsulta(r);
        if (r?.itens) {
            const inicial = {};
            r.itens.forEach((i) => {
                inicial[i.produto_id] = {
                    resposta: i.resposta || '',
                    quantidade: i.quantidade ?? '',
                };
            });
            setRespostas(inicial);
        }
        setCarregando(false);
    }, [hash]);

    useEffect(() => { carregar(); }, [carregar]);

    const alterar = (produtoId, campo, valor) => {
        setRespostas((atual) => ({
            ...atual,
            [produtoId]: { ...(atual[produtoId] || {}), [campo]: valor },
        }));
    };

    const gravar = async (produtoId, resposta) => {
        const atual = respostas[produtoId] || {};

        if (resposta === 'tem' && !(Number(atual.quantidade) > 0)) {
            alterar(produtoId, 'resposta', 'tem');
            toast({ title: 'Informe quantas unidades você tem.', status: 'info', isClosable: true });
            return;
        }

        setSalvando(produtoId);
        const r = await responderConsultaEstoque(hash, {
            produto_id: produtoId,
            resposta,
            quantidade: resposta === 'tem' ? Number(atual.quantidade) : null,
        });
        setSalvando(null);

        if (r?.erro) {
            toast({ title: r.erro, status: 'error', isClosable: true });
            return;
        }

        alterar(produtoId, 'resposta', resposta);
    };

    // O envio so vale com a consulta inteira respondida: item em branco no
    // meio da lista passava despercebido e o produto ficava bloqueado sem
    // ninguem ter dito nada sobre ele.
    const enviar = async () => {
        const pendente = (consulta.itens || []).find((i) => !estaRespondido(respostas[i.produto_id]));

        if (pendente) {
            setCobrando(true);
            const alvo = cardsRef.current[pendente.produto_id];
            if (alvo?.scrollIntoView) {
                alvo.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            toast({
                id: 'faltam-respostas',
                title: `Faltam ${faltando} item(ns) para responder.`,
                description: 'Marque "Tenho" ou "Não tenho mais" em cada peça destacada.',
                status: 'warning',
                isClosable: true,
            });
            return;
        }

        setEnviando(true);
        const r = await finalizarConsultaEstoque(hash);
        setEnviando(false);

        if (r?.erro) {
            toast({ title: r.erro, status: 'error', isClosable: true });
            return;
        }

        toast({ title: r?.message || 'Respostas enviadas. Obrigado!', status: 'success', duration: 6000, isClosable: true });
        carregar();
    };

    const respondidos = useMemo(() => (
        (consulta?.itens || []).filter((i) => estaRespondido(respostas[i.produto_id])).length
    ), [consulta, respostas]);

    const faltando = (consulta?.itens?.length || 0) - respondidos;

    if (carregando) {
        return (
            <Flex minH="100vh" bg="gray.50" justify="center" align="center">
                <Spinner size="lg" color="#F68633" thickness="3px" />
            </Flex>
        );
    }

    if (!consulta) {
        return (
            <Flex minH="100vh" bg="gray.50" justify="center" align="center" p={6}>
                <Box bg="white" borderWidth="1px" borderRadius="lg" p={8} textAlign="center" maxW="440px">
                    <Box color="gray.300" fontSize="40px" mb={3}><FaBoxOpen style={{ margin: '0 auto' }} /></Box>
                    <Heading size="md" mb={2}>Link não encontrado</Heading>
                    <Text color="gray.600" fontSize="sm">
                        Confira se o endereço veio completo. Se continuar assim, fale com a TAGA.
                    </Text>
                </Box>
            </Flex>
        );
    }

    const total = consulta.itens?.length || 0;
    const fechada = consulta.finalizada || consulta.expirada;

    return (
        <Box bg="gray.50" minH="100vh">
            <ScrollToTop bottom={fechada ? '4' : '110px'} />
            <Container maxW="900px" py={6} px={{ base: 3, sm: 6 }}>
                <Flex align="center" gap={3} pb={4} mb={5} borderBottomWidth="1px" borderColor="gray.200">
                    <Image src="/TAGANOBG.png" alt="TAGA Autopartes" h={{ base: '38px', sm: '46px' }} objectFit="contain" />
                    <Box>
                        <Text fontWeight="bold" color="gray.800" lineHeight="short">TAGA Autopartes</Text>
                        <Text fontSize="xs" color="gray.500">CNPJ 48.921.058/0001-02</Text>
                    </Box>
                </Flex>

                <Heading size="lg" color="#F68633" fontWeight="extrabold">Verificação de estoque</Heading>
                <Text color="gray.700" mt={2}>
                    <b>{consulta.fornecedor}</b>, os itens abaixo saíram do nosso catálogo por indisponibilidade.
                    Confirme quais você tem em estoque hoje.
                </Text>

                {consulta.ensaio && (
                    <Alert status="warning" mt={4} borderRadius="lg">
                        <AlertIcon />
                        <Box>
                            <Text fontWeight="bold" fontSize="sm">Consulta de teste</Text>
                            <Text fontSize="sm">
                                Suas respostas ficam registradas, mas nenhum produto é alterado.
                            </Text>
                        </Box>
                    </Alert>
                )}

                {consulta.expirada && !consulta.finalizada && (
                    <Alert status="error" mt={4} borderRadius="lg">
                        <AlertIcon />
                        <Text fontSize="sm">Este link expirou em {data(consulta.expira_em)}. Fale com a TAGA para receber um novo.</Text>
                    </Alert>
                )}

                {consulta.finalizada && (
                    <Alert status="success" mt={4} borderRadius="lg">
                        <AlertIcon />
                        <Text fontSize="sm">
                            Respostas recebidas em {data(consulta.finalizada_em)}. Obrigado!
                        </Text>
                    </Alert>
                )}

                <VStack align="stretch" spacing={3} mt={5}>
                    {(consulta.itens || []).map((item) => {
                        const r = respostas[item.produto_id] || {};
                        const tem = r.resposta === 'tem';
                        const naoTem = r.resposta === 'nao_tem';
                        const pendente = cobrando && !fechada && !estaRespondido(r);

                        return (
                            <Box
                                key={item.produto_id}
                                ref={(el) => { cardsRef.current[item.produto_id] = el; }}
                                bg="white" borderWidth={pendente ? '2px' : '1px'} borderRadius="lg" p={4}
                                borderColor={pendente ? 'red.400' : tem ? 'green.200' : naoTem ? 'gray.300' : 'gray.200'}
                                boxShadow={pendente ? '0 0 0 3px rgba(245,101,101,0.15)' : undefined}
                            >
                                <Flex gap={3} align="start">
                                    <Box flexShrink={0} borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
                                        <ProdutoImagem produto={{ produto_id: item.produto_id }} origem={null} width="64px" height="64px" />
                                    </Box>
                                    <Box minW={0} flex={1}>
                                        <Text fontWeight="semibold" fontSize="sm" wordBreak="break-word">
                                            {item.nome}
                                            <BotaoCopiar
                                                rotulo="Nome"
                                                valor={item.nome}
                                                copiado={copiado === `${item.produto_id}:Nome`}
                                                onCopiar={() => copiar(`${item.produto_id}:Nome`, 'Nome', item.nome)}
                                            />
                                        </Text>
                                        <Box mt={1}>
                                            {[
                                                ['Código Fornecedor', item.codigo_fornecedor],
                                                ['Código Global', item.codigo_global],
                                                ['Marca', item.marca],
                                                ['Valor', emReais(item.valor_compra)],
                                            ].filter(([, valor]) => valor).map(([rotulo, valor]) => (
                                                <Text key={rotulo} fontSize="xs" color="gray.600" wordBreak="break-word" lineHeight="1.9">
                                                    {rotulo}: <Text as="span" fontWeight="semibold" color="gray.800">{valor}</Text>
                                                    <BotaoCopiar
                                                        rotulo={rotulo}
                                                        valor={valor}
                                                        copiado={copiado === `${item.produto_id}:${rotulo}`}
                                                        onCopiar={() => copiar(`${item.produto_id}:${rotulo}`, rotulo, valor)}
                                                    />
                                                </Text>
                                            ))}
                                        </Box>
                                        <Text fontSize="xs" color="gray.500" mt={1}>Fora do ar {tempoParado(item.bloqueado_em)}</Text>
                                    </Box>
                                </Flex>

                                {!fechada && (
                                    <>
                                        <HStack spacing={2} mt={3}>
                                            <Button
                                                size={{ base: 'md', sm: 'sm' }} flex={1} px={2}
                                                leftIcon={<FaCheck />} iconSpacing={{ base: 1, sm: 2 }}
                                                colorScheme="green" variant={tem ? 'solid' : 'outline'}
                                                isLoading={salvando === item.produto_id && !naoTem}
                                                onClick={() => gravar(item.produto_id, 'tem')}
                                            >
                                                <Text as="span" fontSize={{ base: 'sm', sm: 'md' }}>Tenho</Text>
                                            </Button>
                                            <Button
                                                size={{ base: 'md', sm: 'sm' }} flex={1} px={2}
                                                leftIcon={<FaTimes />} iconSpacing={{ base: 1, sm: 2 }}
                                                colorScheme="red" variant={naoTem ? 'solid' : 'outline'}
                                                isLoading={salvando === item.produto_id && naoTem}
                                                onClick={() => gravar(item.produto_id, 'nao_tem')}
                                            >
                                                <Text as="span" fontSize={{ base: 'sm', sm: 'md' }}>Não tenho mais</Text>
                                            </Button>
                                        </HStack>

                                        {(tem || r.resposta === 'tem') && (
                                            <Box mt={3} maxW="160px">
                                                <Text fontSize="xs" color={pendente ? 'red.500' : 'gray.600'} mb={1} fontWeight={pendente ? 'semibold' : 'normal'}>
                                                    {pendente ? 'Informe a quantidade' : 'Quantas unidades?'}
                                                </Text>
                                                <NumberInput
                                                    size={{ base: 'md', sm: 'sm' }} min={1} max={100000}
                                                    value={r.quantidade ?? ''}
                                                    onChange={(v) => alterar(item.produto_id, 'quantidade', somenteInteiro(v))}
                                                >
                                                    <NumberInputField
                                                        placeholder="0"
                                                        inputMode="numeric"
                                                        borderColor={pendente ? 'red.300' : undefined}
                                                        onBlur={() => gravar(item.produto_id, 'tem')}
                                                    />
                                                </NumberInput>
                                            </Box>
                                        )}
                                    </>
                                )}

                                {fechada && r.resposta && (
                                    <Text fontSize="sm" color="gray.600" mt={3}>
                                        Você respondeu: <b>{tem ? `tenho ${r.quantidade} unidade(s)` : 'não tenho mais'}</b>
                                    </Text>
                                )}
                            </Box>
                        );
                    })}
                </VStack>

                {!fechada && (
                    <Box
                        bg="white" borderWidth="1px" borderRadius="lg" p={4} mt={5}
                        position="sticky" bottom={4} boxShadow="lg"
                        borderColor={faltando === 0 ? 'green.300' : 'gray.200'}
                    >
                        <Flex gap={3} align="center" justify="space-between" wrap="wrap" mb={3}>
                            <HStack spacing={2}>
                                {faltando === 0
                                    ? <Box color="green.500"><FaCheck /></Box>
                                    : <Box color="orange.400"><FaExclamationCircle /></Box>}
                                <Text fontSize="sm" fontWeight="medium" color="gray.700">
                                    {faltando === 0
                                        ? `Tudo respondido (${total} de ${total})`
                                        : `${respondidos} de ${total} respondidos`}
                                </Text>
                            </HStack>
                            <Button
                                colorScheme={faltando === 0 ? 'orange' : 'gray'}
                                bg={faltando === 0 ? '#F68633' : 'gray.200'}
                                color={faltando === 0 ? 'white' : 'gray.600'}
                                _hover={faltando === 0 ? { bg: '#e0762a' } : { bg: 'gray.300' }}
                                onClick={enviar} isLoading={enviando} loadingText="Enviando..."
                                w={{ base: '100%', sm: 'auto' }}
                                size="lg"
                            >
                                {faltando === 0 ? 'Enviar respostas' : `Falta responder ${faltando}`}
                            </Button>
                        </Flex>

                        <Progress
                            value={total ? (respondidos / total) * 100 : 0}
                            size="sm" borderRadius="full"
                            colorScheme={faltando === 0 ? 'green' : 'orange'}
                        />

                        {faltando > 0 && (
                            <Text fontSize="xs" color="gray.500" mt={2}>
                                Responda todas as peças para enviar. Toque no botão acima que a gente leva você até a que falta.
                            </Text>
                        )}
                    </Box>
                )}
            </Container>
        </Box>
    );
}
